import axios from 'axios';
import {getShareUrls, prepareAttachmentSize, prepareAttachmentURLs} from '../models';
import {ShareErrorsService} from '../share-errors.service';

const DEFAULT_ATTACHMENT_ROLE = 'attachment'
const ATTACHMENT_STATUS_COMPLETED = 4
const MAX_ATTACHMENT_FOR_UPLOAD = 5

enum BLOT_TYPES {
  image = 'image',
}

const IMAGE_MIME_TYPES = [
  'image/gif',
  'image/jpeg',
  'image/pjpeg',
  'image/png',
  'image/svg+xml',
  'image/tiff',
  'image/vnd.microsoft.icon',
  'image/vnd.wap.wbmp',
  'image/webp',
]

interface IEditorFile {
  name?: string;
  size?: number;
  type?: string;
  previewUrl?: string;
  instance?: File;
  attachmentGlobalId?: string;
  isUploaded?: boolean;
  source?: {
    attachmentId: string;
    workspaceId: string;
  };
}

export interface IUploadingMeta {
  isImport?: boolean;
  inList?: boolean;
  noteId?: string;
  folderId?: string;
  workspaceId?: string;
  attachmentId?: string;
  role?: string;
  emitEvent?: boolean;
}

export function randomString(length, chars = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ') {
  let result = ''
  while (result.length < length) {
    result += chars[Math.round(Math.random() * (chars.length - 1))]
  }
  return result
}

export class Task {
  id = randomString(6)
  aborted: boolean = false
  abortable: boolean = true

  onAbort: () => void
  onStarted: () => void
  onFinished: (result: any) => void
  onProgress: (progress: { total: number; loaded: number }) => void

  promise

  constructor(private callback, private file?) {}

  run() {
    this.promise = this.callback.call(this)
    if (this.onStarted) {
      this.onStarted()
    }
    this.promise.then((result) => {
      if (this.onFinished) {
        this.onFinished(result)
      }
    });
    return this.promise
  }

  getResultPromise() {
    return this.promise
  }

  abort() {
    if (this.aborted) {
      return
    }
    this.aborted = true
    if (this.onAbort) {
      this.onAbort()
    }
  }
}

export class AttachmentManagerPlugin {
  constructor(
    private attachments: any[],
    private AttachmentNotFoundError,
    private _shareErrorsService: ShareErrorsService,
    private shareApiService,
    private gatewayUrl,
    private fileServerUrl,
    private workspaceId,
    private noteId,
    private shareId,
    private shareSecurityKey,
    private videoEditUrl,
    private shareUrl,
    /**
     * Whether the share opened from portals.
     */
    private isWordpress: boolean
  ) {}

  private blotType: string;

  private attachmentToEditorModel(attachment) {
    return {
      ...attachment,
      global_id: attachment.globalId,
      display_name: attachment.displayName,
      date_added: attachment.dateAdded,
      date_updated: attachment.dateUpdated,
      is_encrypted: attachment.isEncrypted,
      file_uuid: attachment.storedFileUUID,
      in_list: attachment.inList,
      location: attachment.attachmentURL,
      url: this.fileServerUrl + attachment.attachmentURL,
    }
  }

  async createTempAttachment(file, task = null) {
    const source = axios.CancelToken.source()
    task.onAbort = () => source.cancel()
    const formData = new FormData()
    formData.append('file', file, file.name)
    try {
      const url = `${this.gatewayUrl}/ft:share/${this.shareId}/uploadFile?securityKey=${this.shareSecurityKey}`
      const response = await axios.post(url, formData, {
        onUploadProgress: (progress): void => {
          if (task && task.onProgress) {
            task.onProgress(progress)
          }
        },
        cancelToken: source.token
      })
      return response.data
    } catch (error) {
      console.log(error)
      return null
    }
  }

  async createAttachment(tempfileName: string, blotId: string) {
    const payload = {
      securityKey: this.shareSecurityKey,
      tempfileName,
      blotId,
      sourceApp: this.isWordpress ? 'uploader_portal_share' : 'uploader_share',
    }
    try {
      const url = `${this.gatewayUrl}/ft:share/${this.shareId}/createUploaderAttachment`
      const response = await axios.post(url, payload)
      return response.data
    } catch (error) {
      console.log(error)
      return null
    }
  }

  async fetchShare() {
    try {
      const url = `${this.shareApiService}/shares/${this.shareId}?securityKey=${this.shareSecurityKey}`
      const response = await axios.get(url)
      return response.data
    } catch (error) {
      console.log(error)
      return null
    }
  }

  async resolve(attachmentId) {
    const attachment = this.attachments.find((attachment) => attachment.globalId === attachmentId)

    if (!attachment) {
      throw new this.AttachmentNotFoundError()
    }
    console.log('Resolved attach', attachment)
    return this.attachmentToEditorModel(attachment)
  }

  private filterImages(files) {
    return files.filter((file) => {
      return IMAGE_MIME_TYPES.includes(file.type)
    });
  }

  validateFiles(files): Promise<boolean> {
    const preparedFiles = this.blotType === BLOT_TYPES.image ? this.filterImages(files) : files
    if (preparedFiles.length > 0) {
      return this.checkFilesBeforeUpload(this.htmlFilesToFiles(preparedFiles))
        .then((files) => {
          const filesCount = files.length
          if(filesCount > MAX_ATTACHMENT_FOR_UPLOAD) {
            this._shareErrorsService.showAttachmentsCountLimitError(filesCount, MAX_ATTACHMENT_FOR_UPLOAD)
            return false
          }
          return files.length ? files.slice(0, MAX_ATTACHMENT_FOR_UPLOAD - 1) : false
        })
        .catch(() => false)
    }
  }

  checkFilesBeforeUpload(inputFiles = []): Promise<any> {
    return new Promise((resolve) => {
      return resolve(inputFiles)
    })
  }

  async chooseFile(options): Promise<IEditorFile> {
    options = options || {}

    this.blotType = options.blotName

    return new Promise((resolve, reject) => {
      const input = document.createElement('input')
      input.setAttribute('type', 'file')

      if (options.blotName === BLOT_TYPES.image) {
        input.setAttribute('accept', 'image/*')
      }

      if (options.accept) {
        input.setAttribute('accept', options.accept)
      }

      if (options.multiple) {
        input.setAttribute('multiple', 'true')
      }

      input.click()
      let processed = false
      input.onchange = () => {
        if (processed) {
          return
        }
        processed = true
        resolve(this.htmlFilesToFiles(input.files))
      }
      setTimeout(() => {
        document.addEventListener(
          'mousedown',
          function onFocus() {
            document.removeEventListener('mousedown', onFocus, false)
            if (processed) {
              return
            }
            processed = true
            resolve(null)
          },
          false
        );
      }, 0)
    })
  }

  private htmlFilesToFiles(htmlFiles) {
    return Array.from(htmlFiles).map((file: any) => {
      const { name, size, type } = file
      return {
        name,
        size,
        type,
        instance: file,
        attachmentGlobalId: randomString(16),
        isUploaded: false,
      }
    })
  }

  async uploadAttachment(noteId, file, blotId, inList, task, options: { emitEvent?: boolean } = {}): Promise<any> {
    const attachmentId = file.attachmentGlobalId || randomString(16)
    const workspaceId = this.workspaceId
    const role = file.role || DEFAULT_ATTACHMENT_ROLE
    const uploadingMeta = {
      attachmentId,
      workspaceId,
      noteId,
      inList,
      role,
      ...options,
    }

    if (!file.instance) {
      return null
    }

    return this.uploadSmallFile(uploadingMeta, file.instance, blotId, task)
  }

  async uploadSmallFile(meta, file, blotId, task) {
    try {
      const tempFile = await this.createTempAttachment(file, task)
      const tempfileName = tempFile ? tempFile.tempfileName : null
      if(!tempfileName) {
        console.log('Empty response tempfileName for /uploadFile')
        return null
      }


      const createdAttachment = await this.createAttachment(tempfileName, blotId)
      const globalId = createdAttachment ? createdAttachment.globalId : null
      if(!globalId) {
        throw new Error('Empty response globalId for /createUploaderAttachment')
      }

      const shareItem = await this.fetchShare()
      if(!shareItem) {
        throw new Error('Empty response shareItem')
      }

      if(!shareItem.note) {
        throw new Error('Empty response shareItem.note')
      }

      const attachments = shareItem.note.attachments || []
      const instance = attachments.find(attachment => attachment.globalId === globalId)
      if(!instance) {
        throw new Error('Uploaded attachment not found for shared note')
      }

      const urls = getShareUrls({
        id: this.shareId,
        securityKey: this.shareSecurityKey,
      }, shareItem.note.globalId)

      const attachment = prepareAttachmentSize(prepareAttachmentURLs({
        attachmentBase: urls.attachmentBase,
        videoAttachmentBase: urls.videoAttachmentBase,
        audioAttachmentBase: urls.audioAttachmentBase,
        imageAttachmentBase: urls.imageAttachmentBase,
        unknownAttachmentBase: urls.unknownAttachmentBase,
        shareUrl: urls.shareUrl,
      }, instance))

      this.addAttachment(meta.noteId, attachment)
      return attachment
    } catch (error) {
      if (error.status === -1) {
        if (meta.inList) {
          return 'TASK_ABORTED'
        } else {
          throw new Error('TASK_ABORTED')
        }
      }
      throw error
    }
  }

  addAttachment(noteId, server_attachment) {
    let isAttachmentFound = false
    for (let i = this.attachments.length - 1; i >= 0; i--) {
      if (this.attachments[i].globalId == server_attachment.globalId) {
        const attachment = this.attachments[i]
        attachment.status = ATTACHMENT_STATUS_COMPLETED
        delete attachment.progress
        this.attachments[i] = Object.assign(this.attachments[i], server_attachment)
        isAttachmentFound = true
        break
      }
    }
    if (!isAttachmentFound) {
      this.attachments.push(server_attachment)
    }
  }

  saveFile(file, options: { blotName?: string, blotId?: string } = {}) {
    const task = new Task(async () => {
      try {
        const isBookmark = options && options.blotName === 'bookmark'
        const uploadOptions: { emitEvent?: boolean } = {}
        if (isBookmark) {
          uploadOptions.emitEvent = false
        }
        const attachment = await this.uploadAttachment(this.noteId, file, options.blotId, false, task, uploadOptions)
        if(!attachment) {
          return null
        }

        return {
          globalId: attachment.globalId,
        }
      } catch (error) {
        throw error;
      }
    }, file)
    return task
  }

  download({ global_id: attachmentId }) {
    const attachment = this.attachments.find((attachment) => attachment.globalId === attachmentId)
    if (!attachment) return false

    const downloadLink = this.fileServerUrl + attachment.attachmentURL + '?dw'
    window.location.href = downloadLink
    return true
  }

  editAttachment (blotId, attachmentId) {
    window.open(`${this.videoEditUrl}?workspaceId=${this.workspaceId}&attachmentGlobalId=${attachmentId}&blotid=${blotId}&url=${this.shareUrl}`, '_self')
  }
}
